import { queryOptions, useQuery } from '@tanstack/react-query';

import { QueryRefetchInterval } from '@cast/shared-types';
import {
  WorkloadOptimizationAgentStatus,
  WorkloadOptimizationAgentStatusResponse,
} from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const WOOP_AGENT_STATUS_QUERY_KEY = 'woop-agent-status';

const getWorkloadOptimizationAgentStatusQueryKey = (clusterId?: string) =>
  [WOOP_AGENT_STATUS_QUERY_KEY, clusterId] as const;

type UseAgentStatusQueryArgs = {
  clusterId: string;
  enabled?: boolean;
  refetchInterval?: QueryRefetchInterval<WorkloadOptimizationAgentStatusResponse>;
  skipDemoMiddleware?: boolean;
};

const getWorkloadOptimizationAgentStatusQueryOptions = ({
  clusterId,
  enabled = true,
  refetchInterval,
  skipDemoMiddleware,
}: UseAgentStatusQueryArgs) =>
  queryOptions<WorkloadOptimizationAgentStatusResponse>({
    queryKey: getWorkloadOptimizationAgentStatusQueryKey(clusterId),
    queryFn: async () => {
      const { data } = await apiClient.workloadOptimization.getAgentStatus(
        { clusterId },
        {
          middlewareArgs: {
            skipMiddleware: [skipDemoMiddleware && 'demo-middleware'],
          },
        }
      );
      return {
        ...data,
        status: data.status || WorkloadOptimizationAgentStatus.UNKNOWN,
      };
    },
    enabled,
    refetchInterval,
  });

export const useWorkloadOptimizationAgentStatusQuery = (
  args: UseAgentStatusQueryArgs
) => {
  const { isLoading, data, error, refetch } = useQuery(
    getWorkloadOptimizationAgentStatusQueryOptions(args)
  );
  return {
    isLoading,
    data,
    error,
    refetch,
  };
};
